import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight, Check, Save, Info, Sparkles, Zap, Rocket } from 'lucide-react';

const SaaSOnboardingWizard = ({ onComplete, onCancel, onSaveDraft, initialData = null, initialStep = 0 }) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [slideDirection, setSlideDirection] = useState('next');
  const [errors, setErrors] = useState({});
  const scrollContainerRef = useRef(null);
  const [formData, setFormData] = useState(initialData || {
    // Step 1: Account & Business Info
    first_name: '',
    last_name: '',
    email: '',
    business_name: '',
    project_name: '',  // New field for project name
    business_website: '',
    phone_number: '',
    discovery_source: '',
    
    // Step 2: Web App Setup Type
    setup_type: '',
    
    // Step 3: Platform Hosting Plan
    hosting_plan: '',
    
    // Step 4: Ongoing Support Subscription
    support_plan: '',
    
    // Step 5: Google & SEO Configuration
    google_business_connected: '',
    google_business_url: '',
    google_analytics_connected: '',
    google_analytics_id: '',
    local_seo_enabled: '',
    target_keywords: '',
    
    // Step 6: Branding Assets
    has_logo: '',
    has_website_copy: '',
    has_brand_colors: '',
    brand_colors: '',
    reference_websites: '',
    
    // Step 7: Core Messaging
    value_proposition: '',
    xyz_statement: '',
    
    // Step 8: Launch & Project Notes
    target_launch_date: '',
    configuration_notes: '',
    internal_notes: ''
  });

  const setupTypes = [
    {
      id: 'essentials',
      name: 'Essentials',
      icon: '✨',
      price: '$2,500 – $5,000',
      features: [
        'Product discovery & technical planning',
        'Core SaaS application (single app)',
        'Authentication & user roles',
        'Basic dashboard & core workflows',
        'Responsive web app (desktop + mobile)',
        'Secure cloud setup (single environment)',
        'Basic integrations (Stripe, email, auth)',
        'Deployment & launch support'
      ],
      description: 'Best for early-stage startups & MVPs',
      timeline: '4–8 weeks'
    },
    {
      id: 'plus',
      name: 'Plus',
      icon: '⚡',
      price: '$5,000 – $7,500',
      features: [
        'Everything in Essentials, plus:',
        'Advanced user roles & permissions',
        'Subscription billing & plans',
        'Scalable backend architecture',
        'Admin panel & analytics',
        'Third-party integrations (CRM, APIs, webhooks)',
        'Performance optimization',
        'Security best practices & monitoring',
        'Multi-environment setup (dev, staging, prod)',
        'Post-launch support window'
      ],
      description: 'Best for growing SaaS products & funded startups',
      timeline: '8–14 weeks',
      recommended: true
    },
    {
      id: 'pro',
      name: 'PRO',
      icon: '👑',
      price: 'Custom Pricing',
      features: [
        'Everything in Plus, plus:',
        'Custom SaaS architecture & system design',
        'Multi-tenant infrastructure',
        'Advanced analytics & reporting',
        'High-availability & auto-scaling cloud setup',
        'Compliance readiness (SOC2, HIPAA, etc.)',
        'Custom integrations & microservices',
        'Dedicated senior engineering team',
        'Long-term roadmap planning',
        'Priority support & ongoing development'
      ],
      description: 'Best for high-growth, enterprise, or complex SaaS platforms',
      timeline: '3–6+ months'
    }
  ];

  const hostingPlans = [
    {
      id: 'essentials',
      name: 'Essentials',
      icon: '☁️',
      price: '$55/month',
      tagline: 'Best for MVPs & early-stage products',
      features: [
        'Single cloud environment (production)',
        'Managed hosting + deployment pipeline (basic)',
        'Standard monitoring + uptime checks',
        'Automated backups (daily)',
        'SSL + basic security hardening',
        'Basic CDN + caching',
        'Email delivery setup (transactional)',
        'Monthly patching & dependency updates (light)'
      ],
      idealFor: 'Low-to-moderate traffic SaaS, MVP launch, internal tools'
    },
    {
      id: 'plus',
      name: 'Plus',
      icon: '⚡',
      price: '$75/month',
      tagline: 'Best for growing SaaS products',
      includePrevious: 'Essentials',
      features: [
        'Multi-environment setup (dev + staging + production)',
        'Improved observability (logs + performance monitoring)',
        'Database optimization + tuning',
        'Automated backups (daily + point-in-time where supported)',
        'WAF / enhanced security protections',
        'CI/CD with rollback strategy',
        'Cost monitoring + optimization checks',
        'Higher performance infrastructure baseline'
      ],
      idealFor: 'Revenue-generating SaaS, integrations, increasing user load',
      recommended: true
    },
    {
      id: 'pro',
      name: 'PRO',
      icon: '👑',
      price: 'Custom Pricing',
      tagline: 'Best for enterprise & high-availability SaaS',
      includePrevious: 'Plus',
      features: [
        'High availability architecture (redundancy + failover)',
        'Auto-scaling infrastructure',
        'Advanced security posture (SSO/SAML-ready, IAM hardening)',
        '24/7 monitoring & alerting options',
        'Disaster recovery planning + regular restore testing',
        'Dedicated database cluster options',
        'Compliance-ready hosting patterns (SOC2/HIPAA support)',
        'Performance SLAs & priority incident response options'
      ],
      idealFor: 'Mission-critical SaaS, enterprise customers, compliance-driven orgs'
    }
  ];

  const supportPlans = [
    {
      id: 'essentials',
      name: 'Essentials',
      icon: '🛟',
      price: '$64/month',
      tagline: 'Best for MVPs & early-stage SaaS products',
      features: [
        'Email-based support',
        'Bug fixes & minor issue resolution',
        'Basic monitoring review',
        'Monthly system health check',
        'Dependency updates & security patches',
        'Up to 10 hours/month of support & maintenance',
        'Standard response times (business hours)'
      ],
      idealFor: 'MVPs, internal tools, low-complexity SaaS platforms'
    },
    {
      id: 'plus',
      name: 'Plus',
      icon: '⚡',
      price: '$144/month',
      tagline: 'Best for growing & revenue-generating SaaS',
      includePrevious: 'Essentials',
      features: [
        'Priority support queue',
        'Feature enhancements & refinements',
        'Performance tuning & optimization',
        'Proactive monitoring & alert review',
        'Monthly reporting & recommendations',
        'Up to 25 hours/month of support & development',
        'Faster response times'
      ],
      idealFor: 'Customer-facing SaaS, growing user bases, integrations',
      recommended: true
    },
    {
      id: 'pro',
      name: 'PRO',
      icon: '👑',
      price: 'Custom Pricing',
      tagline: 'Best for enterprise & mission-critical SaaS',
      includePrevious: 'Plus',
      features: [
        'Dedicated support channel',
        'Ongoing feature development',
        'Architecture reviews & scaling strategy',
        'Advanced security reviews',
        'Incident response & root cause analysis',
        'SLA-based response times',
        'Up to 60+ hours/month of senior engineering support',
        'Roadmap planning & technical leadership'
      ],
      idealFor: 'Enterprise SaaS, compliance-driven platforms, high availability systems'
    },
    {
      id: 'none',
      name: 'Hourly (No Subscription)',
      icon: '⏱️',
      price: '$175 – $225/hour',
      tagline: 'Best for ad-hoc needs & occasional support',
      features: [
        'Pay-as-you-go engineering support',
        'Bug fixes & troubleshooting',
        'Minor feature updates',
        'Performance investigations',
        'Security patching & updates',
        'No monthly commitment'
      ],
      idealFor: 'Short-term or infrequent needs',
      notes: 'Billed in minimum 1-hour increments. Subject to availability. No guaranteed response times or SLAs.'
    }
  ];

  const steps = [
    { id: 'account', title: 'Account & Business Info', icon: '👤' },
    { id: 'setup', title: 'Web App Setup Type', icon: '🎨' },
    { id: 'hosting', title: 'Platform Hosting', icon: '☁️' },
    { id: 'support', title: 'Ongoing Support', icon: '🛟' },
    { id: 'seo', title: 'Google & SEO', icon: '🔍' },
    { id: 'branding', title: 'Branding Assets', icon: '🎨' },
    { id: 'messaging', title: 'Core Messaging', icon: '💬' },
    { id: 'launch', title: 'Launch Details', icon: '🚀' }
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const validateStep = (stepIndex) => {
    const newErrors = {};
    
    switch (stepIndex) {
      case 0: // Account & Business Info
        if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
        if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
        if (!formData.business_name.trim()) newErrors.business_name = 'Business name is required';
        if (!formData.project_name.trim()) newErrors.project_name = 'Project name is required';
        break;
      case 1: // Web App Setup Type
        if (!formData.setup_type) newErrors.setup_type = 'Please select a setup type';
        break;
      case 2: // Platform Hosting Plan
        if (!formData.hosting_plan) newErrors.hosting_plan = 'Please select a hosting plan';
        break;
      case 3: // Ongoing Support
        if (!formData.support_plan) newErrors.support_plan = 'Please select a support plan';
        break;
      case 4: // Google & SEO
        if (!formData.google_business_connected) newErrors.google_business_connected = 'Required';
        if (formData.google_business_connected === 'yes' && !formData.google_business_url.trim()) {
          newErrors.google_business_url = 'URL is required when connected';
        }
        if (!formData.google_analytics_connected) newErrors.google_analytics_connected = 'Required';
        if (formData.google_analytics_connected === 'yes' && !formData.google_analytics_id.trim()) {
          newErrors.google_analytics_id = 'Measurement ID is required';
        }
        if (!formData.local_seo_enabled) newErrors.local_seo_enabled = 'Required';
        if (formData.local_seo_enabled === 'yes' && !formData.target_keywords.trim()) {
          newErrors.target_keywords = 'Keywords are required when SEO is enabled';
        }
        break;
      case 5: // Branding Assets
        if (!formData.has_logo) newErrors.has_logo = 'Required';
        if (!formData.has_website_copy) newErrors.has_website_copy = 'Required';
        if (!formData.has_brand_colors) newErrors.has_brand_colors = 'Required';
        if (formData.has_brand_colors === 'yes' && !formData.brand_colors.trim()) {
          newErrors.brand_colors = 'Colors are required';
        }
        break;
      case 6: // Core Messaging
        if (!formData.value_proposition.trim()) newErrors.value_proposition = 'Value proposition is required';
        if (!formData.xyz_statement.trim()) newErrors.xyz_statement = 'XYZ statement is required';
        break;
      case 7: // Launch Details
        if (!formData.target_launch_date) newErrors.target_launch_date = 'Launch date is required';
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const canProceed = () => {
    return validateStep(currentStep);
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (isLastStep) {
        onComplete(formData);
      } else {
        setSlideDirection('next');
        setCurrentStep(currentStep + 1);
        // Scroll the modal container to top
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setSlideDirection('prev');
      setCurrentStep(currentStep - 1);
      setErrors({});
      // Scroll the modal container to top
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleSaveDraft = () => {
    onSaveDraft(formData, currentStep);
  };

  // Handle clickable step navigation - validates all steps before target
  const handleStepClick = (targetStep) => {
    // Always allow going back to previous steps
    if (targetStep < currentStep) {
      setSlideDirection('prev');
      setCurrentStep(targetStep);
      setErrors({});
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    
    // For forward navigation, validate all steps from current to target-1
    for (let step = currentStep; step < targetStep; step++) {
      if (!validateStep(step)) {
        // Show error on the first invalid step and navigate there
        if (step !== currentStep) {
          setSlideDirection('next');
          setCurrentStep(step);
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }
        return; // Stop navigation, validation errors will be shown
      }
    }
    
    // All steps validated, proceed to target
    setSlideDirection('next');
    setCurrentStep(targetStep);
    setErrors({});
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Keyboard shortcuts - defined after all handlers
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      }
      if (e.key === 'ArrowRight' && !isLastStep && canProceed()) {
        handleNext();
      }
      if (e.key === 'ArrowLeft' && !isFirstStep) {
        handleBack();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSaveDraft();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentStep, formData, isLastStep, isFirstStep, onCancel]);

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-semibold text-carbon-black mb-1.5 sm:mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => updateField('first_name', e.target.value)}
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 ${errors.first_name ? 'border-red-500' : 'border-cool-gray'} focus:outline-none focus:ring-2 focus:ring-fearless-blue`}
                  placeholder="John"
                  data-testid="first-name-input"
                />
                {errors.first_name && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.first_name}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-carbon-black mb-1.5 sm:mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => updateField('last_name', e.target.value)}
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 ${errors.last_name ? 'border-red-500' : 'border-cool-gray'} focus:outline-none focus:ring-2 focus:ring-fearless-blue`}
                  placeholder="Doe"
                  data-testid="last-name-input"
                />
                {errors.last_name && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.last_name}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-carbon-black mb-1.5 sm:mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 ${errors.email ? 'border-red-500' : 'border-cool-gray'} focus:outline-none focus:ring-2 focus:ring-fearless-blue`}
                placeholder="john@company.com"
                data-testid="email-input"
              />
              {errors.email && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-carbon-black mb-1.5 sm:mb-2">
                Business Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.business_name}
                onChange={(e) => updateField('business_name', e.target.value)}
                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 ${errors.business_name ? 'border-red-500' : 'border-cool-gray'} focus:outline-none focus:ring-2 focus:ring-fearless-blue`}
                placeholder="Acme Corporation"
                data-testid="business-name-input"
              />
              {errors.business_name && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.business_name}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-carbon-black mb-1.5 sm:mb-2">
                Project Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.project_name}
                onChange={(e) => updateField('project_name', e.target.value)}
                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 ${errors.project_name ? 'border-red-500' : 'border-cool-gray'} focus:outline-none focus:ring-2 focus:ring-fearless-blue`}
                placeholder="E-commerce Platform Redesign"
                data-testid="project-name-input"
              />
              <p className="text-xs text-slate-gray mt-1">This will be the name of your project in our system</p>
              {errors.project_name && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.project_name}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-semibold text-carbon-black mb-1.5 sm:mb-2">
                  Website <span className="text-slate-gray text-xs">(Optional)</span>
                </label>
                <input
                  type="url"
                  value={formData.business_website}
                  onChange={(e) => updateField('business_website', e.target.value)}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue"
                  placeholder="https://acme.com"
                  data-testid="business-website-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-carbon-black mb-1.5 sm:mb-2">
                  Phone <span className="text-slate-gray text-xs">(Optional)</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone_number}
                  onChange={(e) => updateField('phone_number', e.target.value)}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue"
                  placeholder="+1 (555) 000-0000"
                  data-testid="phone-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-carbon-black mb-1.5 sm:mb-2">
                How did you find us? <span className="text-slate-gray text-xs">(Optional)</span>
              </label>
              <Select value={formData.discovery_source} onValueChange={(value) => updateField('discovery_source', value)}>
                <SelectTrigger className="w-full text-sm sm:text-base" data-testid="discovery-select">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="google">Google Search</SelectItem>
                  <SelectItem value="social">Social Media</SelectItem>
                  <SelectItem value="referral">Referral</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            {errors.setup_type && <p className="text-red-500 text-xs sm:text-sm">{errors.setup_type}</p>}
            <div className="grid grid-cols-1 gap-3 sm:gap-4">
              {setupTypes.map((setup) => (
                <button
                  key={setup.id}
                  onClick={() => updateField('setup_type', setup.id)}
                  className={`relative p-4 sm:p-6 rounded-xl border-2 text-left transition-all hover:shadow-lg ${
                    formData.setup_type === setup.id
                      ? 'border-fearless-blue bg-blue-50 shadow-md'
                      : 'border-cool-gray hover:border-fearless-blue'
                  }`}
                  data-testid={`setup-${setup.id}`}
                >
                  {setup.recommended && (
                    <div className="absolute -top-3 right-4 bg-gradient-to-r from-fearless-blue to-electric-indigo text-white px-2 sm:px-3 py-1 rounded-full text-xs font-bold">
                      MOST POPULAR
                    </div>
                  )}
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="text-2xl sm:text-3xl">{setup.icon}</div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0 mb-2">
                        <h3 className="text-lg sm:text-xl font-bold text-carbon-black">{setup.name}</h3>
                        <div className="text-lg sm:text-xl font-bold text-fearless-blue">{setup.price}</div>
                      </div>
                      <p className="text-xs sm:text-sm text-slate-gray mb-2">{setup.description}</p>
                      <div className="flex items-center text-xs sm:text-sm text-electric-indigo font-medium mb-3">
                        <span>Timeline: {setup.timeline}</span>
                      </div>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-x-4 sm:gap-y-1">
                        {setup.features.slice(0, 6).map((feature, idx) => (
                          <li key={idx} className="flex items-start text-xs sm:text-sm text-slate-gray">
                            <Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 mr-1 sm:mr-2 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      {setup.features.length > 6 && (
                        <p className="text-xs text-electric-indigo mt-2">+ {setup.features.length - 6} more features</p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            {errors.hosting_plan && <p className="text-red-500 text-xs sm:text-sm">{errors.hosting_plan}</p>}
            <div className="grid grid-cols-1 gap-3 sm:gap-4">
              {hostingPlans.map((plan) => (
                <button
                  key={plan.id}
                  onClick={() => updateField('hosting_plan', plan.id)}
                  className={`relative p-4 sm:p-6 rounded-xl border-2 text-left transition-all hover:shadow-lg ${
                    formData.hosting_plan === plan.id
                      ? 'border-fearless-blue bg-blue-50 shadow-md'
                      : 'border-cool-gray hover:border-fearless-blue'
                  }`}
                  data-testid={`hosting-${plan.id}`}
                >
                  {plan.recommended && (
                    <div className="absolute -top-3 right-4 bg-gradient-to-r from-fearless-blue to-electric-indigo text-white px-2 sm:px-3 py-1 rounded-full text-xs font-bold">
                      MOST POPULAR
                    </div>
                  )}
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="text-2xl sm:text-3xl">{plan.icon}</div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0 mb-2">
                        <h3 className="text-lg sm:text-xl font-bold text-carbon-black">{plan.name}</h3>
                        <div className="text-lg sm:text-xl font-bold text-fearless-blue">{plan.price}</div>
                      </div>
                      <p className="text-xs sm:text-sm text-slate-gray mb-2">{plan.tagline}</p>
                      {plan.includePrevious && (
                        <p className="text-xs sm:text-sm text-electric-indigo font-medium mb-2">
                          Includes everything in {plan.includePrevious}, plus:
                        </p>
                      )}
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-x-4 sm:gap-y-1 mb-3">
                        {plan.features.slice(0, 6).map((feature, idx) => (
                          <li key={idx} className="flex items-start text-xs sm:text-sm text-slate-gray">
                            <Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 mr-1 sm:mr-2 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      {plan.features.length > 6 && (
                        <p className="text-xs text-electric-indigo mb-2">+ {plan.features.length - 6} more features</p>
                      )}
                      <p className="text-xs text-slate-gray italic">
                        <span className="font-medium">Ideal for:</span> {plan.idealFor}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            {errors.support_plan && <p className="text-red-500 text-xs sm:text-sm">{errors.support_plan}</p>}
            <div className="grid grid-cols-1 gap-3 sm:gap-4">
              {supportPlans.map((plan) => (
                <button
                  key={plan.id}
                  onClick={() => updateField('support_plan', plan.id)}
                  className={`relative p-4 sm:p-6 rounded-xl border-2 text-left transition-all hover:shadow-lg ${
                    formData.support_plan === plan.id
                      ? 'border-fearless-blue bg-blue-50 shadow-md'
                      : 'border-cool-gray hover:border-fearless-blue'
                  }`}
                  data-testid={`support-${plan.id}`}
                >
                  {plan.recommended && (
                    <div className="absolute -top-3 right-4 bg-gradient-to-r from-fearless-blue to-electric-indigo text-white px-2 sm:px-3 py-1 rounded-full text-xs font-bold">
                      MOST POPULAR
                    </div>
                  )}
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="text-2xl sm:text-3xl">{plan.icon}</div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0 mb-2">
                        <h3 className="text-lg sm:text-xl font-bold text-carbon-black">{plan.name}</h3>
                        <div className="text-lg sm:text-xl font-bold text-fearless-blue">{plan.price}</div>
                      </div>
                      <p className="text-xs sm:text-sm text-slate-gray mb-2">{plan.tagline}</p>
                      {plan.includePrevious && (
                        <p className="text-xs sm:text-sm text-electric-indigo font-medium mb-2">
                          Includes everything in {plan.includePrevious}, plus:
                        </p>
                      )}
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-x-4 sm:gap-y-1 mb-3">
                        {plan.features.slice(0, 6).map((feature, idx) => (
                          <li key={idx} className="flex items-start text-xs sm:text-sm text-slate-gray">
                            <Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 mr-1 sm:mr-2 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      {plan.features.length > 6 && (
                        <p className="text-xs text-electric-indigo mb-2">+ {plan.features.length - 6} more features</p>
                      )}
                      <p className="text-xs text-slate-gray italic">
                        <span className="font-medium">Ideal for:</span> {plan.idealFor}
                      </p>
                      {plan.notes && (
                        <p className="text-xs text-amber-600 mt-2 bg-amber-50 p-2 rounded">
                          <span className="font-medium">Note:</span> {plan.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-semibold text-carbon-black mb-1.5 sm:mb-2">
                Google Business Profile Connected? <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-3 sm:space-x-4">
                <button
                  onClick={() => updateField('google_business_connected', 'yes')}
                  className={`flex-1 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg border-2 font-semibold transition-all text-sm sm:text-base ${
                    formData.google_business_connected === 'yes'
                      ? 'border-fearless-blue bg-blue-50 text-fearless-blue'
                      : 'border-cool-gray text-slate-gray hover:border-fearless-blue'
                  }`}
                >
                  Yes
                </button>
                <button
                  onClick={() => updateField('google_business_connected', 'no')}
                  className={`flex-1 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg border-2 font-semibold transition-all text-sm sm:text-base ${
                    formData.google_business_connected === 'no'
                      ? 'border-fearless-blue bg-blue-50 text-fearless-blue'
                      : 'border-cool-gray text-slate-gray hover:border-fearless-blue'
                  }`}
                >
                  No
                </button>
              </div>
              {errors.google_business_connected && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.google_business_connected}</p>}
            </div>

            {formData.google_business_connected === 'yes' && (
              <div className="animate-fadeIn">
                <label className="block text-sm font-semibold text-carbon-black mb-1.5 sm:mb-2">
                  Google Business Profile URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={formData.google_business_url}
                  onChange={(e) => updateField('google_business_url', e.target.value)}
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 ${errors.google_business_url ? 'border-red-500' : 'border-cool-gray'} focus:outline-none focus:ring-2 focus:ring-fearless-blue`}
                  placeholder="https://g.page/your-business"
                />
                {errors.google_business_url && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.google_business_url}</p>}
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-carbon-black mb-1.5 sm:mb-2">
                Google Analytics Connected? <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-3 sm:space-x-4">
                <button
                  onClick={() => updateField('google_analytics_connected', 'yes')}
                  className={`flex-1 px-6 py-3 rounded-lg border-2 font-semibold transition-all ${
                    formData.google_analytics_connected === 'yes'
                      ? 'border-fearless-blue bg-blue-50 text-fearless-blue'
                      : 'border-cool-gray text-slate-gray hover:border-fearless-blue'
                  }`}
                >
                  Yes
                </button>
                <button
                  onClick={() => updateField('google_analytics_connected', 'no')}
                  className={`flex-1 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg border-2 font-semibold transition-all text-sm sm:text-base ${
                    formData.google_analytics_connected === 'no'
                      ? 'border-fearless-blue bg-blue-50 text-fearless-blue'
                      : 'border-cool-gray text-slate-gray hover:border-fearless-blue'
                  }`}
                >
                  No
                </button>
              </div>
            </div>

            {formData.google_analytics_connected === 'yes' && (
              <div className="animate-fadeIn">
                <label className="block text-sm font-semibold text-carbon-black mb-1.5 sm:mb-2">
                  Google Analytics ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.google_analytics_id}
                  onChange={(e) => updateField('google_analytics_id', e.target.value)}
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 ${errors.google_analytics_id ? 'border-red-500' : 'border-cool-gray'} focus:outline-none focus:ring-2 focus:ring-fearless-blue`}
                  placeholder="G-XXXXXXXXXX"
                />
                {errors.google_analytics_id && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.google_analytics_id}</p>}
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-carbon-black mb-1.5 sm:mb-2">
                Enable Local SEO Optimization? <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-3 sm:space-x-4">
                <button
                  onClick={() => updateField('local_seo_enabled', 'yes')}
                  className={`flex-1 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg border-2 font-semibold transition-all text-sm sm:text-base ${
                    formData.local_seo_enabled === 'yes'
                      ? 'border-fearless-blue bg-blue-50 text-fearless-blue'
                      : 'border-cool-gray text-slate-gray hover:border-fearless-blue'
                  }`}
                >
                  Yes
                </button>
                <button
                  onClick={() => updateField('local_seo_enabled', 'no')}
                  className={`flex-1 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg border-2 font-semibold transition-all text-sm sm:text-base ${
                    formData.local_seo_enabled === 'no'
                      ? 'border-fearless-blue bg-blue-50 text-fearless-blue'
                      : 'border-cool-gray text-slate-gray hover:border-fearless-blue'
                  }`}
                >
                  No
                </button>
              </div>
            </div>

            {formData.local_seo_enabled === 'yes' && (
              <div className="animate-fadeIn">
                <label className="block text-sm font-semibold text-carbon-black mb-1.5 sm:mb-2">
                  Target Keywords <span className="text-red-500">*</span>
                  <span className="text-slate-gray text-xs block mt-1">Enter 5-7 keyword phrases, one per line</span>
                </label>
                <textarea
                  value={formData.target_keywords}
                  onChange={(e) => updateField('target_keywords', e.target.value)}
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 ${errors.target_keywords ? 'border-red-500' : 'border-cool-gray'} focus:outline-none focus:ring-2 focus:ring-fearless-blue resize-none`}
                  rows="4"
                  placeholder="web development services&#10;custom software solutions&#10;mobile app development"
                />
                {errors.target_keywords && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.target_keywords}</p>}
              </div>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-semibold text-carbon-black mb-1.5 sm:mb-2">
                Do you have a logo? <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-3 sm:space-x-4">
                <button
                  onClick={() => updateField('has_logo', 'yes')}
                  className={`flex-1 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg border-2 font-semibold transition-all text-sm sm:text-base ${
                    formData.has_logo === 'yes'
                      ? 'border-fearless-blue bg-blue-50 text-fearless-blue'
                      : 'border-cool-gray text-slate-gray hover:border-fearless-blue'
                  }`}
                >
                  Yes
                </button>
                <button
                  onClick={() => updateField('has_logo', 'no')}
                  className={`flex-1 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg border-2 font-semibold transition-all text-sm sm:text-base ${
                    formData.has_logo === 'no'
                      ? 'border-fearless-blue bg-blue-50 text-fearless-blue'
                      : 'border-cool-gray text-slate-gray hover:border-fearless-blue'
                  }`}
                >
                  No
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-carbon-black mb-2">
                Website Copy Available? <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-4">
                <button
                  onClick={() => updateField('has_website_copy', 'yes')}
                  className={`flex-1 px-6 py-3 rounded-lg border-2 font-semibold transition-all ${
                    formData.has_website_copy === 'yes'
                      ? 'border-fearless-blue bg-blue-50 text-fearless-blue'
                      : 'border-cool-gray text-slate-gray hover:border-fearless-blue'
                  }`}
                >
                  Yes
                </button>
                <button
                  onClick={() => updateField('has_website_copy', 'no')}
                  className={`flex-1 px-6 py-3 rounded-lg border-2 font-semibold transition-all ${
                    formData.has_website_copy === 'no'
                      ? 'border-fearless-blue bg-blue-50 text-fearless-blue'
                      : 'border-cool-gray text-slate-gray hover:border-fearless-blue'
                  }`}
                >
                  No
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-carbon-black mb-2">
                Brand Colors Defined? <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-4">
                <button
                  onClick={() => updateField('has_brand_colors', 'yes')}
                  className={`flex-1 px-6 py-3 rounded-lg border-2 font-semibold transition-all ${
                    formData.has_brand_colors === 'yes'
                      ? 'border-fearless-blue bg-blue-50 text-fearless-blue'
                      : 'border-cool-gray text-slate-gray hover:border-fearless-blue'
                  }`}
                >
                  Yes
                </button>
                <button
                  onClick={() => updateField('has_brand_colors', 'no')}
                  className={`flex-1 px-6 py-3 rounded-lg border-2 font-semibold transition-all ${
                    formData.has_brand_colors === 'no'
                      ? 'border-fearless-blue bg-blue-50 text-fearless-blue'
                      : 'border-cool-gray text-slate-gray hover:border-fearless-blue'
                  }`}
                >
                  No
                </button>
              </div>
            </div>

            {formData.has_brand_colors === 'yes' && (
              <div className="animate-fadeIn">
                <label className="block text-sm font-semibold text-carbon-black mb-2">
                  Brand Colors <span className="text-red-500">*</span>
                  <span className="text-slate-gray text-xs block mt-1">Enter up to 3 colors (hex codes or names)</span>
                </label>
                <input
                  type="text"
                  value={formData.brand_colors}
                  onChange={(e) => updateField('brand_colors', e.target.value)}
                  className={`w-full px-4 py-3 text-base rounded-lg border-2 ${errors.brand_colors ? 'border-red-500' : 'border-cool-gray'} focus:outline-none focus:ring-2 focus:ring-fearless-blue`}
                  placeholder="#4F8DF7, #5B3DF5, #3A1FD8"
                />
                {errors.brand_colors && <p className="text-red-500 text-sm mt-1">{errors.brand_colors}</p>}
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-carbon-black mb-2">
                Reference Websites <span className="text-slate-gray text-xs">(Optional)</span>
                <span className="text-slate-gray text-xs block mt-1">Share URLs of websites you like, one per line</span>
              </label>
              <textarea
                value={formData.reference_websites}
                onChange={(e) => updateField('reference_websites', e.target.value)}
                className="w-full px-4 py-3 text-base rounded-lg border-2 border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue resize-none"
                rows="4"
                placeholder="https://example1.com&#10;https://example2.com"
              />
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-carbon-black mb-2">
                Short Value Proposition <span className="text-red-500">*</span>
                <span className="text-slate-gray text-xs block mt-1">6-12 words that capture what you do</span>
              </label>
              <input
                type="text"
                value={formData.value_proposition}
                onChange={(e) => updateField('value_proposition', e.target.value)}
                className={`w-full px-4 py-3 text-base rounded-lg border-2 ${errors.value_proposition ? 'border-red-500' : 'border-cool-gray'} focus:outline-none focus:ring-2 focus:ring-fearless-blue`}
                placeholder="Modern technology solutions for growing businesses"
                maxLength={80}
              />
              {errors.value_proposition && <p className="text-red-500 text-sm mt-1">{errors.value_proposition}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-carbon-black mb-2">
                XYZ Statement <span className="text-red-500">*</span>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2 mb-2">
                  <p className="text-sm text-slate-gray">
                    <strong>Template:</strong> "This platform helps <span className="text-fearless-blue font-semibold">(X)</span> do <span className="text-fearless-blue font-semibold">(Y)</span> to achieve <span className="text-fearless-blue font-semibold">(Z)</span>."
                  </p>
                  <p className="text-xs text-slate-gray mt-1">Enter "HELP" if you'd like system assistance</p>
                </div>
              </label>
              <textarea
                value={formData.xyz_statement}
                onChange={(e) => updateField('xyz_statement', e.target.value)}
                className={`w-full px-4 py-3 text-base rounded-lg border-2 ${errors.xyz_statement ? 'border-red-500' : 'border-cool-gray'} focus:outline-none focus:ring-2 focus:ring-fearless-blue resize-none`}
                rows="4"
                placeholder="This platform helps small businesses launch modern websites to grow their online presence and reach more customers."
              />
              {errors.xyz_statement && <p className="text-red-500 text-sm mt-1">{errors.xyz_statement}</p>}
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-carbon-black mb-2">
                Target Launch Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.target_launch_date}
                onChange={(e) => updateField('target_launch_date', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className={`w-full px-4 py-3 text-base rounded-lg border-2 ${errors.target_launch_date ? 'border-red-500' : 'border-cool-gray'} focus:outline-none focus:ring-2 focus:ring-fearless-blue`}
              />
              {errors.target_launch_date && <p className="text-red-500 text-sm mt-1">{errors.target_launch_date}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-carbon-black mb-2">
                Additional Configuration Notes <span className="text-slate-gray text-xs">(Optional)</span>
                <span className="text-slate-gray text-xs block mt-1">Any specific requirements or features you need</span>
              </label>
              <textarea
                value={formData.configuration_notes}
                onChange={(e) => updateField('configuration_notes', e.target.value)}
                className="w-full px-4 py-3 text-base rounded-lg border-2 border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue resize-none"
                rows="5"
                placeholder="e.g., Need e-commerce integration, customer portal, booking system..."
              />
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-start space-x-3">
                <Sparkles className="h-6 w-6 text-fearless-blue flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-carbon-black mb-2">Ready to Launch!</h3>
                  <p className="text-sm text-slate-gray">
                    You're all set! Review your selections and click "Launch Project" to activate your project and begin development.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-6" data-testid="saas-wizard">
      <Card ref={scrollContainerRef} className="w-full max-w-4xl shadow-2xl rounded-2xl border-0 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <CardContent className="p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-4 sm:mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full gradient-fearless flex items-center justify-center text-xl sm:text-2xl flex-shrink-0">
                  {currentStepData.icon}
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-carbon-black truncate">{currentStepData.title}</h2>
                  <p className="text-xs sm:text-sm text-slate-gray">Step {currentStep + 1} of {steps.length}</p>
                </div>
              </div>
              <button
                onClick={onCancel}
                className="text-slate-gray hover:text-carbon-black text-2xl sm:text-3xl leading-none transition-colors flex-shrink-0 ml-2"
                data-testid="wizard-close-btn"
              >
                ×
              </button>
            </div>

            {/* Progress Stepper - Mobile: simplified, Desktop: full */}
            {/* Mobile Progress Bar with Clickable Steps */}
            <div className="sm:hidden mb-4">
              <div className="flex items-center justify-between text-xs text-slate-gray mb-2">
                <span>Progress</span>
                <span>{Math.round(((currentStep + 1) / steps.length) * 100)}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-3">
                <div 
                  className="h-full bg-gradient-to-r from-fearless-blue to-electric-indigo transition-all duration-300"
                  style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                />
              </div>
              {/* Mobile Step Buttons */}
              <div className="flex items-center justify-between gap-1">
                {steps.map((step, index) => (
                  <button
                    key={step.id}
                    onClick={() => handleStepClick(index)}
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                      index < currentStep
                        ? 'bg-green-500 text-white hover:bg-green-600'
                        : index === currentStep
                        ? 'bg-fearless-blue text-white'
                        : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                    }`}
                    data-testid={`wizard-step-mobile-${index + 1}`}
                    title={step.title}
                  >
                    {index < currentStep ? <Check className="h-3 w-3" /> : index + 1}
                  </button>
                ))}
              </div>
            </div>

            {/* Desktop Progress Stepper - Clickable */}
            <div className="hidden sm:flex items-center justify-between mb-6">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center flex-1">
                  <button
                    onClick={() => handleStepClick(index)}
                    className={`flex items-center justify-center w-8 h-8 rounded-full border-2 text-sm font-semibold transition-all cursor-pointer hover:scale-110 ${
                      index < currentStep
                        ? 'bg-green-500 border-green-500 text-white hover:bg-green-600 hover:border-green-600'
                        : index === currentStep
                        ? 'border-fearless-blue text-fearless-blue bg-blue-50'
                        : 'border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-500 hover:bg-gray-50'
                    }`}
                    data-testid={`wizard-step-desktop-${index + 1}`}
                    title={step.title}
                  >
                    {index < currentStep ? <Check className="h-4 w-4" /> : index + 1}
                  </button>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-1 mx-2 transition-all ${
                      index < currentStep ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className={`mb-6 sm:mb-8 transition-all duration-300 ${
            slideDirection === 'next' ? 'animate-slideInRight' : 'animate-slideInLeft'
          }`}>
            {renderStep()}
          </div>

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-4 sm:pt-6 border-t border-cool-gray gap-3 sm:gap-0">
            {/* Mobile: Stack buttons vertically */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:order-1 order-2">
              {!isFirstStep && (
                <Button
                  variant="outline"
                  onClick={handleBack}
                  className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base"
                  data-testid="wizard-back-btn"
                >
                  <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                  Back
                </Button>
              )}
              
              <Button
                variant="outline"
                onClick={handleSaveDraft}
                className="px-4 sm:px-6 py-2 sm:py-3 border-slate-gray text-slate-gray hover:bg-gray-50 text-sm sm:text-base"
                data-testid="wizard-save-draft-btn"
              >
                <Save className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                Save Draft
              </Button>
            </div>

            <Button
              onClick={handleNext}
              className="gradient-fearless text-white px-6 sm:px-8 py-3 text-base sm:text-lg font-semibold sm:order-2 order-1 w-full sm:w-auto"
              data-testid="wizard-next-btn"
            >
              {isLastStep ? (
                <>
                  <Rocket className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Launch Project
                </>
              ) : (
                <>
                  Next Step
                  <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 ml-2" />
                </>
              )}
            </Button>
          </div>

          {/* Helper Text - Hide on mobile */}
          <div className="hidden sm:flex mt-6 items-center justify-center space-x-4 text-xs text-slate-gray">
            <span className="flex items-center">
              <kbd className="px-2 py-1 bg-gray-100 rounded text-xs mr-1">←</kbd>
              <kbd className="px-2 py-1 bg-gray-100 rounded text-xs mr-2">→</kbd>
              Navigate
            </span>
            <span className="flex items-center">
              <kbd className="px-2 py-1 bg-gray-100 rounded text-xs mr-2">Esc</kbd>
              Close
            </span>
            <span className="flex items-center">
              <kbd className="px-2 py-1 bg-gray-100 rounded text-xs mr-2">⌘ S</kbd>
              Save Draft
            </span>
          </div>
        </CardContent>
      </Card>

      <style jsx>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideInRight {
          animation: slideInRight 0.3s ease-out;
        }
        .animate-slideInLeft {
          animation: slideInLeft 0.3s ease-out;
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SaaSOnboardingWizard;
